import { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useLocation, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { supabase } from '@/lib/supabase';
import { useAuth } from '@/hooks/useAuth';
import { mensagemAmigavel } from '@/lib/mensagemAmigavel';
import TextoRico from '@/components/base/TextoRico';
import { guardarDestino } from '@/lib/destino';
import { anotarVisita } from '@/lib/visitante';
import { guardarInscricaoCodigo } from '@/lib/inscricaoCodigo';
import { ehCelular, emApp } from '@/lib/app';
import type {
  EventoPublico,
  Participacao,
  MinhaSituacao,
  EncontroEvento,
} from '@/types/evento-publico';
import LinhaDoTempoDatas from './components/LinhaDoTempoDatas';
import TopoEvento from './components/TopoEvento';
import CartaoQuandoOnde from './components/CartaoQuandoOnde';
import DescricaoEvento from './components/DescricaoEvento';
import EscolherParticipacao, {
  chaveOpcao,
  aceitaComoSuplente,
  rotuloSuplente,
  type ConfigModalidade,
} from './components/EscolherParticipacao';
import RodapeEvento from './components/RodapeEvento';
import BlocoMapa from './components/BlocoMapa';
import ChamarizLoja from '@/pages/loja/components/ChamarizLoja';
import BotaoMeAvise from './components/BotaoMeAvise';
import VisualizadorArte from '@/components/feature/VisualizadorArte';
import { absorveTaxa, buscarTaxa, type TaxaValor } from '@/lib/taxa';
import { formatarCentavos } from '@/pages/ingresso/utils';
import Accordion from '@/components/ui/Accordion';
import Card from '@/components/ui/Card';
import Chip from '@/components/ui/Chip';
import Sheet from '@/components/ui/Sheet';
import { formatarPeriodo, formatarValor, formatarHorario, textoDoValor } from './utils';
import { buscarSituacaoInscrito, type SituacaoInscrito } from '@/lib/situacaoInscrito';
import { agruparPorModalidade, filtrarLotes } from './lotes';
import { usePrecoFichas } from './usePrecoFichas';

function linkWhatsApp(telefone: string): string {
  const digitos = telefone.replace(/\D/g, '');
  return `https://wa.me/55${digitos}`;
}

// Data curta para o cartão "Quando": "sáb, 14 nov" (ou faixa quando é
// mais de um dia). A hora entra embaixo, em cinza.
function dataCurtaEvento(inicio: string, fim: string | null): string {
  const a = new Date(inicio);
  const semana = a.toLocaleDateString('pt-BR', { weekday: 'short' }).replace('.', '');
  const dia = a.getDate();
  const mes = a.toLocaleDateString('pt-BR', { month: 'short' }).replace('.', '');

  if (fim) {
    const b = new Date(fim);
    const mesmoDia = a.toDateString() === b.toDateString();
    if (!mesmoDia) {
      const dia2 = b.getDate();
      const mes2 = b.toLocaleDateString('pt-BR', { month: 'short' }).replace('.', '');
      if (a.getMonth() === b.getMonth() && a.getFullYear() === b.getFullYear()) {
        return `${dia} a ${dia2} ${mes}`;
      }
      return `${dia} ${mes} a ${dia2} ${mes2}`;
    }
  }
  return `${semana}, ${dia} ${mes}`;
}

function rotuloTipo(categoria: string | null): string {
  const mapa: Record<string, string> = {
    acampamento: 'Acampamento',
    retiro: 'Retiro',
    curso: 'Curso',
    encontro: 'Encontro',
    congresso: 'Congresso',
    show: 'Show',
    formacao: 'Formação',
    outro: 'Outro',
  };
  return mapa[categoria ?? ''] ?? 'Evento';
}

function textoValorRange(participacoes: Participacao[]): string {
  const pagos = participacoes.filter((p) => p.valor_situacao === 'pago');
  const temGratuito = participacoes.some((p) => p.valor_situacao === 'gratuito');
  const temADefinir = participacoes.some((p) => p.valor_situacao === 'a_definir');

  if (pagos.length === 0) {
    if (temGratuito) return 'Gratuito';
    return 'Valor a definir';
  }

  const valores = pagos.map((p) => p.valor_centavos ?? 0);
  const min = Math.min(...valores);
  const max = Math.max(...valores);
  const faixa = min === max ? formatarValor(min) : `De ${formatarValor(min)} a ${formatarValor(max)}`;

  if (temGratuito && temADefinir) return `${faixa}, gratuito e outros`;
  if (temGratuito) return 'Gratuito e pago';
  if (temADefinir) return `${faixa} e outros a definir`;
  return faixa;
}

function algumaAberta(participacoes: Participacao[]): boolean {
  return participacoes.some((p) => p.situacao === 'abertas');
}

function vagasMaisCritica(participacoes: Participacao[], vendeIngressos: boolean): string | null {
  const abertas = participacoes.filter((p) => p.situacao === 'abertas');
  const comVagas = abertas
    .filter((p) => typeof p.restantes === 'number' && p.restantes >= 0 && p.restantes <= 20)
    .sort((a, b) => (a.restantes ?? 999) - (b.restantes ?? 999));
  if (comVagas.length === 0) return null;
  const v = comVagas[0].restantes ?? 0;
  if (vendeIngressos) return v === 1 ? '1 ingresso disponível' : `${v} ingressos disponíveis`;
  return v === 1 ? 'Resta 1 vaga' : `Restam ${v} vagas`;
}

// "Últimas vagas": só com os números que a consulta já traz (vagas/restantes).
// Restaram 10% ou menos das vagas do lote. Sem número, não mostramos nada.
function temUltimasVagas(participacoes: Participacao[]): boolean {
  return participacoes.some(
    (p) =>
      p.situacao === 'abertas' &&
      typeof p.vagas === 'number' &&
      p.vagas > 0 &&
      typeof p.restantes === 'number' &&
      p.restantes >= 0 &&
      p.restantes <= p.vagas * 0.1,
  );
}

interface ComoParticipar {
  ok: boolean;
  so_divulgacao: boolean;
  entrada_franca?: boolean;
  texto: string | null;
  tem_whatsapp: boolean;
  numero: string | null;
  mensagem: string | null;
  telefone_bonito: string | null;
}

export default function EventoPage() {
  const { slug } = useParams<{ slug: string }>();
  const [searchParams] = useSearchParams();
  const codigo = searchParams.get('c');
  // ?ver=1 = "só quero ver a página do evento" (volta da ficha): não pula
  // direto para a inscrição de novo.
  const verDetalhes = searchParams.get('ver') === '1';
  const navigate = useNavigate();
  const { user } = useAuth();
  const location = useLocation();
  const noAr = (location.state as { noAr?: boolean } | null)?.noAr === true;
  const visitaRegistrada = useRef(false);
  // Só redireciona uma vez por abertura (o efeito roda a cada render).
  const redirecionou = useRef(false);

  const [evento, setEvento] = useState<EventoPublico | null>(null);
  const [participacoes, setParticipacoes] = useState<Participacao[]>([]);
  const [minha, setMinha] = useState<MinhaSituacao | null>(null);
  const [meusIngressos, setMeusIngressos] = useState<{
    quantidadePaga: number;
    pedidoPagoId: number | null;
    pedidoAguardandoId: number | null;
  } | null>(null);
  const [liberadas, setLiberadas] = useState<number[]>([]);
  const [encontros, setEncontros] = useState<EncontroEvento[]>([]);
  const [motivoPorModalidade, setMotivoPorModalidade] = useState<Map<number, string | null>>(
    new Map(),
  );
  const [loading, setLoading] = useState(true);
  const [naoEncontrado, setNaoEncontrado] = useState(false);
  const [erro, setErro] = useState<string | null>(null);
  const [copiado, setCopiado] = useState(false);
  const [compartilharAberto, setCompartilharAberto] = useState(false);
  const [arteAberta, setArteAberta] = useState(false);
  const [comoParticipar, setComoParticipar] = useState<ComoParticipar | null>(null);
  const [ehDono, setEhDono] = useState(false);
  const [escolhida, setEscolhida] = useState<string | null>(null);
  const [avisosAberto, setAvisosAberto] = useState(false);
  const [taxa, setTaxa] = useState<TaxaValor | null>(null);
  // aceita_suplentes / situacao_visivel por modalidade (para o botão de suplente).
  const [configModalidades, setConfigModalidades] = useState<Map<number, ConfigModalidade>>(
    new Map(),
  );
  // O que ESTE inscrito pode ver sobre a inscrição dele neste evento.
  const [sitEvento, setSitEvento] = useState<SituacaoInscrito | null>(null);

  // Só consulta o preço das fichas quando o evento vende na loja.
  const precoFichas = usePrecoFichas(evento?.id ?? null, !!evento?.vende_produtos);

  useEffect(() => {
    let ativo = true;
    async function carregar() {
      setLoading(true);
      setErro(null);
      setNaoEncontrado(false);
      // Sem resposta da função = selo neutro. Limpa o que era de outra abertura.
      setSitEvento(null);
      setConfigModalidades(new Map());
      try {
        const { data: ev, error: errEv } = await supabase
          .from('eventos')
          .select('*')
          .eq('slug', slug)
          .maybeSingle();

        if (errEv) throw errEv;
        if (!ev) {
          if (ativo) setNaoEncontrado(true);
          return;
        }

        if (ativo) setEvento(ev as EventoPublico);

        // É dono? Só ele vê a prévia de um evento em rascunho.
        if (user) setEhDono((ev as EventoPublico).dono_id === user.id);

        // Distingue "só divulgação" dos demais fluxos. Se a chamada falhar,
        // seguimos como se não fosse "só divulgação" (nada muda na página).
        try {
          const { data: comoData } = await supabase.rpc('como_participar_do_evento', {
            p_slug: slug,
          });
          if (ativo) setComoParticipar((comoData as ComoParticipar | null) ?? null);
        } catch {
          /* mantém comoParticipar nulo */
        }

        const { data: partes, error: errPartes } = await supabase
          .from('vw_participacoes')
          .select('*')
          .eq('evento_id', ev.id)
          .order('ordem_modalidade', { ascending: true })
          .order('ordem_grupo', { ascending: true });

        if (errPartes) throw errPartes;
        if (ativo) setParticipacoes((partes as Participacao[]) || []);

        // Configuração de vagas/suplentes de cada modalidade do evento.
        const modIds = [
          ...new Set(((partes as Participacao[]) || []).map((p) => p.modalidade_id)),
        ];
        const mapaCfg = new Map<number, ConfigModalidade>();
        if (modIds.length > 0) {
          const { data: modsData } = await supabase
            .from('modalidades')
            .select('id, aceita_suplentes, situacao_visivel')
            .in('id', modIds);
          (
            (modsData as {
              id: number;
              aceita_suplentes: boolean | null;
              situacao_visivel: boolean | null;
            }[]) || []
          ).forEach((m) => {
            mapaCfg.set(m.id, {
              aceita_suplentes: m.aceita_suplentes === true,
              situacao_visivel: m.situacao_visivel !== false,
            });
          });
        }
        if (ativo) setConfigModalidades(mapaCfg);

        const { data: encData, error: errEnc } = await supabase
          .from('vw_encontros_evento')
          .select('*')
          .eq('evento_id', ev.id)
          .order('inicio_em');
        if (errEnc) throw errEnc;
        if (ativo) setEncontros((encData as EncontroEvento[]) || []);

        // Motivo de "em breve" por participação (vw_situacao_modalidade).
        const { data: sitData } = await supabase
          .from('vw_situacao_modalidade')
          .select('id, motivo_em_breve')
          .eq('evento_id', ev.id);
        const motivoMap = new Map<number, string | null>();
        ((sitData as { id: number; motivo_em_breve: string | null }[]) || []).forEach((s) => {
          motivoMap.set(s.id, s.motivo_em_breve);
        });
        if (ativo) setMotivoPorModalidade(motivoMap);

        // Situação do usuário logado (vazio se não estiver logado — sem erro)
        const { data: minhaData } = await supabase
          .from('vw_minha_situacao_evento')
          .select('evento_id, eu_estou_inscrito, pessoas_que_inscrevi, meu_codigo, meu_status')
          .eq('evento_id', ev.id)
          .maybeSingle();
        const minhaLinha = (minhaData as MinhaSituacao | null) ?? null;
        if (ativo) setMinha(minhaLinha);

        // Sigilo: o status real só aparece depois da resposta do banco.
        if (user && minhaLinha?.meu_codigo) {
          const { data: insRow } = await supabase
            .from('inscricoes')
            .select('id')
            .eq('codigo', minhaLinha.meu_codigo)
            .maybeSingle();
          const insId = (insRow as { id: number } | null)?.id;
          if (insId) {
            const sit = await buscarSituacaoInscrito(insId).catch(() => null);
            if (ativo) setSitEvento(sit);
          }
        }

        // Ingressos da pessoa neste evento (todos os status).
        const { data: ingData } = await supabase
          .from('vw_meus_ingressos')
          .select('pedido_id, status_pedido')
          .eq('evento_id', ev.id);
        let qtdPaga = 0;
        let pedidoPago: number | null = null;
        let pedidoAguardando: number | null = null;
        ((ingData as { pedido_id: number; status_pedido: string }[]) || []).forEach((ing) => {
          if (ing.status_pedido === 'pago') {
            qtdPaga += 1;
            if (pedidoPago == null || ing.pedido_id > pedidoPago) pedidoPago = ing.pedido_id;
          } else if (ing.status_pedido === 'aguardando') {
            if (pedidoAguardando == null || ing.pedido_id > pedidoAguardando) {
              pedidoAguardando = ing.pedido_id;
            }
          }
        });
        if (ativo) {
          setMeusIngressos({
            quantidadePaga: qtdPaga,
            pedidoPagoId: pedidoPago,
            pedidoAguardandoId: pedidoAguardando,
          });
        }

        if (codigo) {
          const { data: libs, error: errLibs } = await supabase.rpc('modalidades_liberadas', {
            p_evento_slug: slug,
            p_codigo: codigo,
          });
          if (errLibs) throw errLibs;
          const ids = (libs as number[]) || [];
          if (ativo) setLiberadas(ids);
          // Guarda o código do link: a inscrição segue sem o app por 12 h.
          if (slug && ids.length > 0) guardarInscricaoCodigo(slug, codigo, ids);
        }
      } catch (e) {
        if (ativo) {
          setErro(
            mensagemAmigavel(e, 'Não foi possível carregar este evento. Tente novamente.'),
          );
        }
      } finally {
        if (ativo) setLoading(false);
      }
    }
    carregar();
    return () => {
      ativo = false;
    };
  }, [slug, codigo, user]);

  // ---- Participações visíveis (públicas ou liberadas por código) ----
  const visiveis = participacoes.filter((p) => p.publica || liberadas.includes(p.modalidade_id));

  const gruposModalidade = useMemo(() => {
    const arr = agruparPorModalidade(visiveis);
    // Aberto com ?c=CODE: a participação liberada por esse código vem primeiro.
    if (codigo && liberadas.length > 0) {
      const lib = new Set(liberadas);
      arr.sort(
        (a, b) =>
          (lib.has(a[0].modalidade_id) ? 0 : 1) - (lib.has(b[0].modalidade_id) ? 0 : 1),
      );
    }
    return arr;
  }, [visiveis, codigo, liberadas]);

  // Lotes: mostra só o primeiro à venda e esconde os seguintes.
  // Com código liberado mantemos a ordem especial (link por convite).
  const { grupos: gruposCompra, proximoLote } = useMemo(() => {
    if (codigo && liberadas.length > 0) return { grupos: gruposModalidade, proximo: null };
    return filtrarLotes(gruposModalidade, !!evento?.vende_ingressos);
  }, [gruposModalidade, evento?.vende_ingressos, codigo, liberadas]);

  const participacoesCompra = useMemo(() => gruposCompra.flat(), [gruposCompra]);

  // Taxa de serviço da opção que está marcada (mesma fonte da tela de compra).
  const opcaoParaTaxa = useMemo(() => {
    const abertas = participacoesCompra.filter((p) => p.situacao === 'abertas');
    return abertas.find((p) => chaveOpcao(p) === escolhida) ?? abertas[0] ?? null;
  }, [participacoesCompra, escolhida]);
  const absorve = absorveTaxa(evento?.quem_paga_taxa);

  useEffect(() => {
    let ativo = true;
    if (!evento || absorve || !opcaoParaTaxa) {
      setTaxa(null);
      return () => {
        ativo = false;
      };
    }
    const passou = evento.fim_em ? new Date(evento.fim_em) < new Date() : false;
    if (evento.status === 'cancelado' || passou) {
      setTaxa(null);
      return () => {
        ativo = false;
      };
    }
    buscarTaxa(opcaoParaTaxa.modalidade_id, opcaoParaTaxa.opcao_id).then((t) => {
      if (ativo) setTaxa(t);
    });
    return () => {
      ativo = false;
    };
  }, [evento, absorve, opcaoParaTaxa]);

  // Seleciona a primeira opção aberta por padrão (só aparência).
  useEffect(() => {
    if (escolhida || !evento) return;
    const desab =
      evento.status === 'cancelado' || (evento.fim_em ? new Date(evento.fim_em) < new Date() : false);
    if (desab) return;
    const primeira =
      participacoesCompra.find((p) => p.situacao === 'abertas') ??
      participacoesCompra.find((p) =>
        aceitaComoSuplente(p, configModalidades.get(p.modalidade_id)),
      );
    if (primeira) setEscolhida(chaveOpcao(primeira));
  }, [participacoesCompra, evento, escolhida, configModalidades]);

  // Anota a visita uma única vez por carregamento, depois que a página já
  // apareceu. Fogo e esquece: é só um contador, nunca pode atrapalhar quem
  // quer se inscrever. Páginas de rascunho não contam.
  useEffect(() => {
    if (visitaRegistrada.current) return;
    if (!evento || evento.status === 'rascunho') return;
    visitaRegistrada.current = true;
    anotarVisita(evento.slug);
  }, [evento]);

  const copiarLink = async () => {
    try {
      await navigator.clipboard.writeText(window.location.href);
    } catch {
      // silencioso
    }
    setCopiado(true);
    setTimeout(() => setCopiado(false), 2000);
  };

  const compartilhar = async () => {
    const url = window.location.href;
    if (navigator.share) {
      try {
        await navigator.share({ title: evento?.nome ?? 'Evento', url });
        return;
      } catch {
        // cai no fallback
      }
    }
    try {
      await navigator.clipboard.writeText(url);
    } catch {
      // silencioso
    }
    setCopiado(true);
    setTimeout(() => setCopiado(false), 2000);
  };

  const compartilharWhatsApp = () => {
    const url = window.location.href;
    const texto = encodeURIComponent(`Confira este evento: ${evento?.nome ?? 'Evento'}\n${url}`);
    window.open(`https://wa.me/?text=${texto}`, '_blank', 'noopener,noreferrer');
  };

  const irParaInscricao = (
    p: Participacao,
    opts?: { replace?: boolean; comOpcao?: boolean },
  ) => {
    const compra = !!evento?.vende_ingressos;
    const base = compra ? `/ingresso/${p.modalidade_id}` : `/inscricao/${p.modalidade_id}`;
    // A opção marcada na página do evento (Masculino/Feminino) vai junto: a
    // ficha abre já com ela escolhida, sem perguntar duas vezes.
    const comOpcao = opts?.comOpcao !== false && p.opcao_id != null;
    const destino = comOpcao ? `${base}?opcao=${p.opcao_id}` : base;
    const replace = opts?.replace === true;
    // Comprar ingresso não exige login — vai direto.
    if (compra) {
      navigate(destino, { replace });
      return;
    }
    // Celular sem o app: a inscrição só acontece no app instalado. Vai direto
    // para a tela de inscrição e o portão de instalação aparece no lugar dela.
    if (ehCelular() && !emApp()) {
      navigate(base, { replace });
      return;
    }
    if (user) {
      navigate(destino, { replace });
    } else {
      guardarDestino(destino);
      navigate(`/entrar?voltar=${encodeURIComponent(destino)}`, { replace: true });
    }
  };

  // ---- Link de inscrição com código (?c=) ---------------------------------
  // Se o código libera uma modalidade e ela é a ÚNICA com inscrição aberta
  // (as outras encerradas, esgotadas ou ocultas), cai direto na tela de
  // inscrição — no mesmo ponto em que o "Quero ir" levaria (login/app
  // obrigatório continuam iguais), sem empilhar histórico.
  useEffect(() => {
    if (redirecionou.current) return;
    if (!codigo || verDetalhes) return;
    if (loading || !evento || liberadas.length === 0) return;

    const cancelado = evento.status === 'cancelado';
    const fimPassou = evento.fim_em ? new Date(evento.fim_em) < new Date() : false;
    if (cancelado || fimPassou) return;
    if (
      comoParticipar?.ok === true &&
      (comoParticipar.so_divulgacao || comoParticipar.entrada_franca)
    ) {
      return;
    }
    if (evento.vende_ingressos && meusIngressos?.pedidoAguardandoId != null) return;

    const abertas = visiveis.filter((p) => p.situacao === 'abertas');
    const modalidades = Array.from(new Set(abertas.map((p) => p.modalidade_id)));
    if (modalidades.length !== 1) return;

    const modalidadeId = modalidades[0];
    if (!liberadas.includes(modalidadeId)) return;

    const daModalidade = abertas.filter((p) => p.modalidade_id === modalidadeId);
    const opcoesAbertas = daModalidade.filter((p) => p.opcao_id != null);
    const alvo = opcoesAbertas.length > 0 ? opcoesAbertas[0] : daModalidade[0];
    redirecionou.current = true;
    // Só pré-seleciona a opção quando não há dúvida (uma única aberta).
    irParaInscricao(alvo, { replace: true, comOpcao: opcoesAbertas.length === 1 });
  }, [
    codigo,
    verDetalhes,
    loading,
    evento,
    liberadas,
    visiveis,
    comoParticipar,
    meusIngressos,
    irParaInscricao,
  ]);

  if (loading) {
    return (
      <div className="min-h-screen bg-background-100">
        <div className="h-[300px] w-full animate-pulse bg-background-200/70" />
        <div className="mx-auto w-full max-w-[720px] px-5">
          <div className="-mt-4 rounded-t-[16px] bg-background-100 pt-7">
            <div className="grid grid-cols-2 gap-3">
              <div className="h-24 animate-pulse rounded-[18px] bg-background-200/70" />
              <div className="h-24 animate-pulse rounded-[18px] bg-background-200/70" />
            </div>
            <div className="mt-4 h-40 animate-pulse rounded-[18px] bg-background-200/70" />
          </div>
        </div>
      </div>
    );
  }

  if (naoEncontrado) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-background-100 px-5 py-8">
        <Card className="w-full max-w-[420px] p-8 text-center">
          <span className="mx-auto flex h-14 w-14 items-center justify-center text-foreground-300">
            <i className="ri-calendar-2-line text-[40px]" aria-hidden />
          </span>
          <h1 className="mt-4 text-[20px] font-extrabold text-foreground-950">
            Não encontramos este evento
          </h1>
          <p className="mt-2 text-[14px] leading-relaxed text-foreground-600">
            O endereço pode estar incorreto ou o evento não está mais disponível.
          </p>
          <Link
            to="/"
            className="aperta mt-6 flex h-14 w-full cursor-pointer items-center justify-center whitespace-nowrap rounded-[18px] bg-primary-500 text-[15px] font-bold text-white"
          >
            Ver todos os eventos
          </Link>
        </Card>
      </div>
    );
  }

  if (erro || !evento) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-background-100 px-5 py-8">
        <Card className="w-full max-w-[420px] p-8 text-center">
          <span className="mx-auto flex h-14 w-14 items-center justify-center text-foreground-300">
            <i className="ri-error-warning-line text-[40px]" aria-hidden />
          </span>
          <h1 className="mt-4 text-[20px] font-extrabold text-foreground-950">
            Não foi possível carregar
          </h1>
          <p className="mt-2 text-[14px] leading-relaxed text-foreground-600">{erro}</p>
          <button
            type="button"
            onClick={() => window.location.reload()}
            className="aperta mt-6 h-14 w-full cursor-pointer whitespace-nowrap rounded-[18px] bg-primary-500 text-[15px] font-bold text-white"
          >
            Tentar novamente
          </button>
        </Card>
      </div>
    );
  }

  const cancelado = evento.status === 'cancelado';
  const fimPassou = evento.fim_em ? new Date(evento.fim_em) < new Date() : false;
  const desabilitado = cancelado || fimPassou;

  const imagemDesktop = evento.imagem_desktop_url;
  const imagemMobile = evento.imagem_mobile_url || evento.imagem_desktop_url;
  const temArte = !!imagemMobile;
  // Maior resolução disponível para o visualizador em tela cheia.
  const capaMaior = evento.imagem_desktop_url || evento.imagem_mobile_url;

  const periodo =
    evento.inicio_em && evento.fim_em ? formatarPeriodo(evento.inicio_em, evento.fim_em) : null;
  const horario = evento.inicio_em ? formatarHorario(evento.inicio_em) : null;
  const dataCompleta = [periodo, horario].filter(Boolean).join(' · ');

  const cidadeTexto = [evento.cidade, evento.uf].filter(Boolean).join(', ');
  const enderecoCompleto = [evento.local_nome, evento.endereco, evento.cidade, evento.uf]
    .filter(Boolean)
    .join(', ');

  const temContato = !!evento.contato_nome || !!evento.contato_telefone;

  const abertasVisiveis = participacoesCompra.filter((p) => p.situacao === 'abertas');
  const textoValorAbertas =
    abertasVisiveis.length > 0 ? textoValorRange(abertasVisiveis) : null;

  const jaInscrito = !!minha?.eu_estou_inscrito;
  // Nunca mostrar o status real antes da resposta: sem resposta = neutro.
  const situacaoSigilosa = !sitEvento || sitEvento.sigilo === true;
  const inscritoSuplente = !situacaoSigilosa && sitEvento?.situacao === 'lista_espera';
  const inscritoPendente = !situacaoSigilosa && sitEvento?.situacao === 'pendente';
  const vendeIngressos = !!evento.vende_ingressos;
  const soDivulgacao = comoParticipar?.ok === true && comoParticipar.so_divulgacao === true;
  const entradaFranca = comoParticipar?.ok === true && comoParticipar.entrada_franca === true;
  const semParticipacao = soDivulgacao || entradaFranca;
  const vendeProdutos = !!evento.vende_produtos;
  // Evento que vende fichas da loja (Quermesse): nada de "Me avise".
  const vendeFichas = vendeProdutos;
  const temIngressoPago = (meusIngressos?.quantidadePaga ?? 0) > 0;
  const temIngressoAguardando = meusIngressos?.pedidoAguardandoId != null;

  const selecionaveis = participacoesCompra.filter(
    (p) =>
      (p.situacao === 'abertas' ||
        aceitaComoSuplente(p, configModalidades.get(p.modalidade_id))) &&
      !desabilitado,
  );
  const opcaoEscolhida =
    selecionaveis.find((p) => chaveOpcao(p) === escolhida) ?? selecionaveis[0] ?? null;
  const escolhidaEsgotada = !!opcaoEscolhida && opcaoEscolhida.situacao === 'esgotado';
  const rotuloOpcao = escolhidaEsgotada
    ? rotuloSuplente(configModalidades.get(opcaoEscolhida!.modalidade_id))
    : vendeIngressos
      ? 'Comprar ingresso'
      : 'Fazer inscrição';
  // Valor ao lado de "Quero ir": o preço da opção que ele vai abrir. Só quando
  // nada pode ser selecionado mostramos a faixa das opções com inscrição aberta.
  const valorRodape = opcaoEscolhida ? textoDoValor(opcaoEscolhida) : textoValorAbertas;
  // Quando o organizador absorve a taxa, nada de "+ taxa".
  const notaTaxa = absorve
    ? null
    : taxa && taxa.taxaCentavos > 0
      ? `+ ${formatarCentavos(taxa.taxaCentavos)} de taxa`
      : '+ taxa de serviço';
  const textoVagasCriticas = vagasMaisCritica(participacoesCompra, vendeIngressos);
  const ultimasVagas = temUltimasVagas(participacoesCompra);
  const inscricoesAbertas = algumaAberta(participacoesCompra);

  // ---- Rodapé fixo (adapta ao estado do evento, mantendo os mesmos fluxos) ----
  let rodape: { totalRotulo?: string; totalValor?: string | null; valorNota?: string | null; botaoTexto: string; botaoIcone?: string; onBotao: () => void; desabilitado?: boolean } | null = null;

  if (soDivulgacao) {
    rodape = null;
  } else if (entradaFranca) {
    rodape = vendeProdutos
      ? {
          totalRotulo: 'Fichas a partir de',
          totalValor: precoFichas != null ? formatarCentavos(precoFichas) : null,
          botaoTexto: 'Comprar fichas',
          botaoIcone: 'ri-coupon-3-line',
          onBotao: () => navigate(`/e/${evento.slug}/fichas`),
        }
      : null;
  } else if (desabilitado) {
    rodape = {
      botaoTexto: cancelado ? 'Evento cancelado' : 'Encerrado',
      onBotao: () => {},
      desabilitado: true,
    };
  } else if (vendeIngressos) {
    if (temIngressoAguardando) {
      rodape = {
        botaoTexto: 'Pagar agora',
        botaoIcone: 'ri-arrow-right-line',
        onBotao: () => navigate(`/pedido/${meusIngressos!.pedidoAguardandoId}`),
      };
    } else if (opcaoEscolhida) {
      rodape = {
        totalValor: valorRodape,
        valorNota: notaTaxa,
        botaoTexto: rotuloOpcao,
        botaoIcone: 'ri-arrow-right-line',
        onBotao: () => irParaInscricao(opcaoEscolhida),
      };
    } else if (temIngressoPago) {
      rodape = {
        botaoTexto: 'Ver meus ingressos',
        botaoIcone: 'ri-ticket-2-line',
        onBotao: () => navigate('/meus-ingressos'),
      };
    }
  } else if (opcaoEscolhida) {
    rodape = {
      totalValor: valorRodape,
      valorNota: notaTaxa,
      botaoTexto: rotuloOpcao,
      botaoIcone: 'ri-arrow-right-line',
      onBotao: () => irParaInscricao(opcaoEscolhida),
    };
  } else if (jaInscrito && minha?.meu_codigo) {
    rodape = {
      botaoTexto: 'Ver minha inscrição',
      botaoIcone: 'ri-file-text-line',
      onBotao: () => navigate(`/minha-inscricao/${minha.meu_codigo}`),
    };
  }

  // Nada para comprar/selecionar: o botão continua visível no celular.
  // Evento que vende fichas nunca mostra "Me avise": leva direto às fichas.
  if (!rodape && !desabilitado) {
    rodape = vendeFichas
      ? {
          totalRotulo: 'Fichas a partir de',
          totalValor: precoFichas != null ? formatarCentavos(precoFichas) : null,
          botaoTexto: 'Comprar fichas',
          botaoIcone: 'ri-coupon-3-line',
          onBotao: () => navigate(`/e/${evento.slug}/fichas`),
        }
      : {
          botaoTexto: 'Me avise',
          botaoIcone: 'ri-notification-3-line',
          onBotao: () => setAvisosAberto(true),
        };
  }

  const categoriaRaw = (evento.categoria ?? '').trim().toLowerCase();
  const categoria = categoriaRaw === 'outro' ? '' : rotuloTipo(evento.categoria);
  const localCurto = [evento.local_nome, cidadeTexto].filter(Boolean).join(' · ') || null;

  const quandoPrincipal = evento.inicio_em
    ? dataCurtaEvento(evento.inicio_em, evento.fim_em)
    : null;
  const quandoSecundario = evento.inicio_em ? formatarHorario(evento.inicio_em) : null;

  // "Datas importantes" só aparece quando existe alguma data ALÉM do dia do
  // próprio evento (encontros no mesmo dia não contam).
  const diaDoEvento = evento.inicio_em ? new Date(evento.inicio_em).toDateString() : null;
  const datasExtras = encontros.filter(
    (e) => !diaDoEvento || new Date(e.inicio_em).toDateString() !== diaDoEvento,
  );

  return (
    <div className={`min-h-screen bg-background-100 ${rodape ? 'pb-28' : ''}`}>
      {evento.status === 'rascunho' && ehDono && (
        <div className="bg-warning-500">
          <p className="mx-auto flex max-w-[720px] items-center gap-2 px-5 py-2.5 text-[13px] font-semibold text-white">
            <span className="flex h-4 w-4 items-center justify-center">
              <i className="ri-eye-line" aria-hidden />
            </span>
            Prévia — só você vê. O evento ainda não foi publicado.
          </p>
        </div>
      )}
      {noAr && (
        <div className="bg-accent-500">
          <p className="mx-auto flex max-w-[720px] items-center gap-2 px-5 py-2.5 text-[13px] font-semibold text-white">
            <span className="flex h-4 w-4 items-center justify-center">
              <i className="ri-rocket-2-line" aria-hidden />
            </span>
            No ar! Compartilhe o link do seu evento.
          </p>
        </div>
      )}

      <TopoEvento
        imagemMobile={imagemMobile}
        imagemDesktop={imagemDesktop}
        nome={evento.nome}
        categoria={categoria}
        seloCancelado={cancelado}
        seloEncerrado={fimPassou}
        onCompartilhar={() => setCompartilharAberto(true)}
        onAbrirArte={() => setArteAberta(true)}
      />

      {/* Folha branca que sobe sobre a capa */}
      <div className="app-entra relative -mt-4 rounded-t-[16px] bg-background-100">
        <main className="mx-auto w-full max-w-[720px] px-5 pt-7">
          {/* Nome + categoria no topo da folha branca (quando há arte, o nome
              não fica mais por cima da capa) */}
          {temArte && (
            <div className="app-entra mb-4">
              {categoria && (
                <p className="text-[11px] font-bold uppercase tracking-[0.18em] text-primary-600">
                  {categoria}
                </p>
              )}
              <h1 className="mt-1 text-[24px] font-extrabold leading-tight text-foreground-950">
                {evento.nome}
              </h1>
              {(cancelado || fimPassou) && (
                <div className="mt-2.5 flex flex-wrap items-center gap-2">
                  {cancelado && (
                    <Chip tipo="neutro">
                      <i className="ri-close-circle-line" aria-hidden />
                      Evento cancelado
                    </Chip>
                  )}
                  {fimPassou && !cancelado && (
                    <Chip tipo="neutro">
                      <i className="ri-time-line" aria-hidden />
                      Já aconteceu
                    </Chip>
                  )}
                </div>
              )}
            </div>
          )}

          {/* Selos de situação */}
          <div className="flex flex-wrap items-center gap-2">
            {entradaFranca && (
              <Chip tipo="success">
                <i className="ri-door-open-line" aria-hidden />
                Entrada franca
              </Chip>
            )}
            {!semParticipacao && !desabilitado && inscricoesAbertas && ultimasVagas && (
              <Chip tipo="warning">
                <i className="ri-fire-line" aria-hidden />
                Últimas vagas
              </Chip>
            )}
            {!semParticipacao &&
              !desabilitado &&
              inscricoesAbertas &&
              !ultimasVagas &&
              textoVagasCriticas && (
                <Chip tipo="warning">
                  <i className="ri-fire-line" aria-hidden />
                  {textoVagasCriticas}
                </Chip>
              )}
            {!semParticipacao &&
              !desabilitado &&
              inscricoesAbertas &&
              !ultimasVagas &&
              !textoVagasCriticas && (
                <Chip tipo="info">
                  <i className="ri-check-line" aria-hidden />
                  {vendeIngressos ? 'Ingressos à venda' : 'Inscrições abertas'}
                </Chip>
              )}
            {temIngressoPago && (
              <Chip tipo="success">
                <i className="ri-ticket-2-line" aria-hidden />
                {meusIngressos!.quantidadePaga === 1
                  ? 'Você tem 1 ingresso'
                  : `Você tem ${meusIngressos!.quantidadePaga} ingressos`}
              </Chip>
            )}
            {temIngressoAguardando && !temIngressoPago && (
              <Chip tipo="warning">
                <i className="ri-time-line" aria-hidden />
                Ingresso aguardando pagamento
              </Chip>
            )}
            {jaInscrito && (
              <Chip
                tipo={
                  situacaoSigilosa
                    ? 'neutro'
                    : inscritoSuplente
                      ? 'neutro'
                      : inscritoPendente
                        ? 'warning'
                        : 'success'
                }
              >
                <i
                  className={
                    situacaoSigilosa
                      ? 'ri-mail-line'
                      : inscritoSuplente
                        ? 'ri-stack-line'
                        : inscritoPendente
                          ? 'ri-time-line'
                          : 'ri-check-line'
                  }
                  aria-hidden
                />
                {situacaoSigilosa
                  ? 'Inscrição recebida'
                  : inscritoSuplente
                    ? 'Você é suplente'
                    : inscritoPendente
                      ? 'Inscrição aguardando pagamento'
                      : 'Você está inscrito'}
              </Chip>
            )}
          </div>

          {/* Me avise + compartilhar (discretos, perto do título) */}
          <div className="mt-3 flex flex-wrap items-center gap-2">
            {!vendeFichas && <BotaoMeAvise eventoId={evento.id} eventoNome={evento.nome} />}
            <button
              type="button"
              onClick={() => setCompartilharAberto(true)}
              className="aperta inline-flex h-9 cursor-pointer items-center justify-center gap-1.5 whitespace-nowrap rounded-full border border-background-300 bg-background-50 px-3 text-[13px] font-semibold text-foreground-800 hover:bg-background-100"
            >
              <span className="flex h-4 w-4 items-center justify-center">
                <i className="ri-share-forward-line text-[15px]" aria-hidden />
              </span>
              Compartilhar
            </button>
          </div>

          {/* Quando / Onde */}
          <div className="mt-4">
            <CartaoQuandoOnde
              quandoPrincipal={quandoPrincipal}
              quandoSecundario={quandoSecundario}
              onde={localCurto}
            />
          </div>

          {/* Descrição */}
          {evento.descricao && (
            <div className="mt-6">
              <DescricaoEvento texto={evento.descricao} />
            </div>
          )}

          {/* Participação */}
          {soDivulgacao ? (
            <div className="mt-6">
              <Card className="app-entra p-5">
                <h2 className="text-[17px] font-extrabold text-foreground-950">
                  Como conseguir a sua
                </h2>
                {comoParticipar?.texto && (
                  <TextoRico
                    texto={comoParticipar.texto}
                    className="mt-3 text-[15px] leading-relaxed text-foreground-700"
                  />
                )}
                {comoParticipar?.tem_whatsapp && comoParticipar.numero && (
                  <>
                    <a
                      href={`https://wa.me/${comoParticipar.numero}?text=${encodeURIComponent(comoParticipar.mensagem ?? '')}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="aperta mt-5 flex h-14 w-full items-center justify-center gap-2 whitespace-nowrap rounded-[18px] bg-[#25D366] text-[15px] font-bold text-white"
                    >
                      <span className="flex h-5 w-5 items-center justify-center">
                        <i className="ri-whatsapp-line" aria-hidden />
                      </span>
                      Chamar no WhatsApp
                    </a>
                    {comoParticipar.telefone_bonito && (
                      <p className="mt-2 text-center text-[13px] text-foreground-500">
                        {comoParticipar.telefone_bonito}
                      </p>
                    )}
                  </>
                )}
              </Card>
            </div>
          ) : entradaFranca ? (
            <div className="mt-6">
              <Card className="app-entra p-5">
                <div className="flex items-center gap-3">
                  <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-[14px] bg-primary-50 text-primary-600">
                    <i className="ri-door-open-line text-[20px]" aria-hidden />
                  </span>
                  <h2 className="text-[17px] font-extrabold text-foreground-950">Entrada franca</h2>
                </div>
                <p className="mt-3 text-[15px] leading-relaxed text-foreground-700">
                  {vendeProdutos
                    ? 'É só chegar! As fichas das barracas você já pode comprar por aqui e retirar lá.'
                    : 'Entrada franca. É só chegar!'}
                </p>
              </Card>
            </div>
          ) : (
            <div className="mt-6">
              <h2 className="text-[17px] font-extrabold text-foreground-950">
                {vendeIngressos ? 'Escolha seu ingresso' : 'Escolha como participar'}
              </h2>
              <div className="mt-3">
                <EscolherParticipacao
                  grupos={gruposCompra}
                  vendeIngressos={vendeIngressos}
                  desabilitado={desabilitado}
                  motivoPorModalidade={motivoPorModalidade}
                  selecionada={escolhida}
                  onSelecionar={setEscolhida}
                  proximoLote={proximoLote}
                  mostraTaxa={!absorve}
                  configPorModalidade={configModalidades}
                />
              </div>
            </div>
          )}

          {/* Loja */}
          {vendeProdutos && !entradaFranca && (
            <div className="mt-6">
              <ChamarizLoja
                eventoId={evento.id}
                eventoNome={evento.nome}
                eventoImagem={evento.imagem_mobile_url || evento.imagem_desktop_url}
                slug={evento.slug}
              />
            </div>
          )}

          {/* Programação / datas */}
          {datasExtras.length > 0 && (
            <div className="mt-6">
              <Accordion titulo="Datas importantes">
                <LinhaDoTempoDatas
                  encontros={datasExtras}
                  eventoNome={evento.nome}
                  eventoInicioEm={evento.inicio_em}
                  eventoFimEm={evento.fim_em}
                  compacto
                />
              </Accordion>
            </div>
          )}

          {/* Localização */}
          {evento.local_nome && (
            <div className="mt-3">
              <Accordion titulo="Onde vai acontecer" resumo={cidadeTexto || undefined}>
                <BlocoMapa
                  localNome={evento.local_nome}
                  endereco={evento.endereco}
                  cidadeTexto={cidadeTexto}
                  cep={evento.cep}
                  enderecoCompleto={enderecoCompleto}
                />
              </Accordion>
            </div>
          )}

          {/* Organizador */}
          {temContato && (
            <div className="mt-3">
              <Accordion titulo="Organizado por">
                <div className="flex items-center gap-3">
                  <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-full bg-primary-50 text-primary-600">
                    <i className="ri-user-3-line text-xl" aria-hidden />
                  </span>
                  <div className="min-w-0">
                    <p className="truncate text-[15px] font-bold text-foreground-950">
                      {evento.contato_nome || 'Organizador do evento'}
                    </p>
                    {evento.contato_telefone && (
                      <a
                        href={linkWhatsApp(evento.contato_telefone)}
                        target="_blank"
                        rel="noopener noreferrer nofollow"
                        className="aperta mt-0.5 inline-flex items-center gap-1.5 text-[13px] font-semibold text-primary-600"
                      >
                        <span className="flex h-4 w-4 items-center justify-center">
                          <i className="ri-whatsapp-line" aria-hidden />
                        </span>
                        Falar no WhatsApp
                      </a>
                    )}
                  </div>
                </div>
              </Accordion>
            </div>
          )}

          {/* Compartilhar (recolhido) */}
          <div className="mt-3">
            <Accordion titulo="Compartilhar este evento">
              <div className="flex flex-col gap-2 sm:flex-row">
                <button
                  type="button"
                  onClick={compartilharWhatsApp}
                  className="aperta flex h-12 flex-1 cursor-pointer items-center justify-center gap-2 whitespace-nowrap rounded-[14px] border border-background-200 bg-background-50 text-[14px] font-semibold text-foreground-800"
                >
                  <span className="flex h-5 w-5 items-center justify-center text-[#25D366]">
                    <i className="ri-whatsapp-line" aria-hidden />
                  </span>
                  WhatsApp
                </button>
                <button
                  type="button"
                  onClick={copiarLink}
                  className="aperta flex h-12 flex-1 cursor-pointer items-center justify-center gap-2 whitespace-nowrap rounded-[14px] border border-background-200 bg-background-50 text-[14px] font-semibold text-foreground-800"
                >
                  <span className="flex h-5 w-5 items-center justify-center text-foreground-500">
                    <i className={copiado ? 'ri-check-line' : 'ri-link'} aria-hidden />
                  </span>
                  {copiado ? 'Copiado!' : 'Copiar link'}
                </button>
              </div>
            </Accordion>
          </div>

          <p className="mt-8 text-center text-[12px] text-foreground-400">
            Se Liga Católico · eventos católicos perto de você
          </p>
        </main>
      </div>

      {/* Rodapé fixo */}
      {rodape && (
        <RodapeEvento
          totalRotulo={rodape.totalRotulo}
          totalValor={rodape.totalValor}
          valorNota={rodape.valorNota}
          botaoTexto={rodape.botaoTexto}
          botaoIcone={rodape.botaoIcone}
          onBotao={rodape.onBotao}
          desabilitado={rodape.desabilitado}
        />
      )}

      {/* Folha de compartilhar */}
      <Sheet
        aberto={compartilharAberto}
        titulo="Compartilhar evento"
        aoFechar={() => setCompartilharAberto(false)}
      >
        <div className="space-y-2">
          <button
            type="button"
            onClick={() => {
              compartilharWhatsApp();
              setCompartilharAberto(false);
            }}
            className="aperta flex w-full cursor-pointer items-center gap-3 rounded-[18px] bg-background-100 px-4 py-3.5 text-left"
          >
            <span className="flex h-10 w-10 items-center justify-center rounded-full bg-[#25D366]/15 text-[#25D366]">
              <i className="ri-whatsapp-line text-xl" aria-hidden />
            </span>
            <span className="text-[15px] font-semibold text-foreground-950">
              Enviar pelo WhatsApp
            </span>
          </button>
          <button
            type="button"
            onClick={() => {
              compartilhar();
              setCompartilharAberto(false);
            }}
            className="aperta flex w-full cursor-pointer items-center gap-3 rounded-[18px] bg-background-100 px-4 py-3.5 text-left"
          >
            <span className="flex h-10 w-10 items-center justify-center rounded-full bg-primary-100 text-primary-600">
              <i className={copiado ? 'ri-check-line text-xl' : 'ri-link text-xl'} aria-hidden />
            </span>
            <span className="text-[15px] font-semibold text-foreground-950">
              {copiado ? 'Link copiado!' : 'Copiar link'}
            </span>
          </button>
        </div>
      </Sheet>

      {/* Me avise (mesmo botão do topo, aberto pelo rodapé fixo) */}
      <Sheet
        aberto={avisosAberto}
        titulo="Me avise"
        aoFechar={() => setAvisosAberto(false)}
      >
        <p className="text-[14px] leading-relaxed text-foreground-700">
          A gente te avisa quando este evento tiver novidade: inscrições, fichas ou ingressos.
        </p>
        <div className="mt-4 flex justify-center">
          <BotaoMeAvise eventoId={evento.id} eventoNome={evento.nome} />
        </div>
        <p className="mt-3 text-center text-[12px] leading-snug text-foreground-500">
          Se os avisos não estiverem disponíveis neste navegador, fale com o organizador.
        </p>
      </Sheet>

      {/* Visualizador da arte em tela cheia */}
      <VisualizadorArte
        aberto={arteAberta}
        imagem={capaMaior}
        alt={evento.nome}
        titulo={evento.nome}
        onFechar={() => setArteAberta(false)}
      />
    </div>
  );
}